import React, { useMemo } from "react"
import { Dialog, Box, Button, Stack, Typography } from "@mui/material"
import "./tokens.css"
import "./ClaimCelebration.css"

// Coupon redemption celebration — adapted from ClaimCelebration but tuned for
// XP accrual: green "XP" badge instead of orange "level" star, xpgreen.png
// in the reward pill, success copy says "XP UNLOCKED".
const CONFETTI_COLORS = ["#FCD5CA", "#03907F", "#92278F", "#FFE9A8", "#AAD4CE", "#F05125"]

function buildConfetti(count, seed) {
    const pieces = []
    for (let i = 0; i < count; i++) {
        const rand = (n) => ((Math.sin(seed + i * 17.3 + n * 31.7) + 1) / 2)
        pieces.push({
            left: rand(1) * 100,
            duration: 3.2 + rand(2) * 3.6,
            delay: -rand(3) * (3.2 + rand(2) * 3.6),
            sizeW: 6 + rand(4) * 8,
            sizeH: 12 + rand(5) * 10,
            skew: rand(6) * 40 - 20,
            color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        })
    }
    return pieces
}

export default function CouponCelebration({ open, onClose, xpAmount, title, description }) {
    const seed = useMemo(() => (open ? Date.now() : 0), [open])
    const confetti = useMemo(() => buildConfetti(80, seed), [seed])

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen
            PaperProps={{
                sx: {
                    background: "radial-gradient(ellipse at top, #03907F 0%, #1d2a28 70%)",
                    color: "#fff",
                    overflow: "hidden",
                    position: "relative",
                },
            }}
        >
            {/* Confetti layer */}
            <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
                {confetti.map((p, i) => (
                    <span
                        key={i}
                        className="xp-confetti"
                        style={{
                            left: `${p.left}%`,
                            width: p.sizeW,
                            height: p.sizeH,
                            background: p.color,
                            animationDuration: `${p.duration}s`,
                            animationDelay: `${p.delay}s`,
                            transform: `skew(${p.skew}deg)`,
                        }}
                    />
                ))}
            </Box>

            <Stack
                alignItems="center"
                justifyContent="center"
                spacing={3}
                sx={{ height: "100%", px: 3, py: 6, position: "relative", zIndex: 1 }}
            >


                {/* Star badge — same animation as level-up but green tint and shows "XP". */}
                <Box className="xp-star-wrap" sx={{ position: "relative", width: 240, height: 240 }}>
                    <svg viewBox="0 0 100 100" width="100%" height="100%">
                        <defs>
                            <radialGradient id="xp-coupon-star-grad" cx="50%" cy="45%" r="60%">
                                <stop offset="0%" stopColor="#7FE6CB" />
                                <stop offset="55%" stopColor="#03907F" />
                                <stop offset="100%" stopColor="#02685B" />
                            </radialGradient>
                        </defs>
                        <path
                            className="xp-star-fill"
                            d="M50 3 L61 37 L97 37 L68 59 L79 93 L50 72 L21 93 L32 59 L3 37 L39 37 Z"
                            fill="url(#xp-coupon-star-grad)"
                        />
                        <path
                            className="xp-star-outline"
                            d="M50 3 L61 37 L97 37 L68 59 L79 93 L50 72 L21 93 L32 59 L3 37 L39 37 Z"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="2.5"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <Typography
                        className="xp-star-number"
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "grid",
                            placeItems: "center",
                            fontSize: 56,
                            fontWeight: 700,
                            color: "#fff",
                            textShadow: "0 2px 10px rgba(0,0,0,0.35)",
                            letterSpacing: ".06em",
                            paddingTop:"24px",
                            paddingLeft:"5px"
                        }}
                    >
                        XP
                    </Typography>
                </Box>

                {title && (
                    <Typography
                        className="xp-fade-in"
                        component={"h3"}
                        sx={{ fontSize: 20, opacity: 1, textAlign: "center", animationDelay: "1300ms", maxWidth: 600 }}
                    >
                        {title}
                    </Typography>
                )}

                {description && (
                    <Typography
                        className="xp-fade-in"
                        sx={{ fontSize: 18, opacity: 1, textAlign: "center", animationDelay: "1400ms", maxWidth: 600 }}
                    >
                        {description}
                    </Typography>
                )}

                {/* Reward pill */}
                <Box
                    className="xp-fade-in"
                    sx={{
                        mt: 2,
                        px: { xs: 3, sm: 5 },
                        py: 2,
                        background: "rgba(255,255,255,0.1)",
                        borderRadius: 999,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        animationDelay: "1450ms",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: ".16em",
                            textTransform: "uppercase",
                            opacity: 0.75,
                        }}
                    >
                        Reward
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <img src="/mascotes/xpgreen.png" alt="xp" width={40} height={40} />
                        <Typography sx={{ fontSize: 32, fontWeight: 700 }}>+{xpAmount}</Typography>
                    </Box>
                </Box>

                <Stack
                    className="xp-fade-in"
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    sx={{ mt: 4, animationDelay: "1600ms" }}
                >
                    <Button
                        onClick={onClose}
                        variant="contained"
                        sx={{
                            bgcolor: "#fff",
                            color: "#1d2a28",
                            textTransform: "uppercase",
                            px: 4,
                            py: 1.5,
                            borderRadius: 999,
                            fontWeight: 700,
                            "&:hover": { bgcolor: "#e6f3ef" },
                        }}
                    >
                        Open My Experience
                    </Button>
                </Stack>
            </Stack>
        </Dialog>
    )
}
